<template>
  <SummaryItem
    :title="$t('ui.envelopes.create.steps.summary.name.title')"
    titleClasses="self-center"
    :class="{'flex-col md:flex-row': showTitleField}"
    controlClasses="w-full md:w-auto md:self-center ml-0 mt-sm md:mt-0"
    dataEc="envelopes_summary_page"
    dataEt="edit_name_button"
  >
    <template v-slot:default>
      <div class="w-full">
        <div v-if="showTitleField" key="input">
          <FloatingLabelInput
            ref="titleInput"
            v-model.trim="title"
            aria-label="title"
            :placeholder="$t('ui.envelopes.create.steps.name.placeholder')"
            :hasError="hasErrors('title')"
            class="mb-0 mt-0 !min-w-0"
            data-ec="envelopes_name_page"
            data-et="name_input"
            @input="validate()"
            @keydown.enter="handleName"
            @keydown.esc="cancelEdit"
          />
          <ValidationError
            :hasError="hasErrors('title')"
            :errorMessage="getError('title')"
            class="w-full mt-sm"
          />
        </div>
        <div v-else key="text">
          {{ title }}
        </div>
      </div>
    </template>
    <template v-slot:controls>
      <div class="flex gap-xs flex-row flex-row-reverse">
        <BaseButton
          size="small"
          variant="secondary"
          :disabled="hasErrors('title') || envelopeSent"
          @click.native="handleName"
          :loading="isUpdatingEnvelope"
        >
          {{ nameButtonText }}
        </BaseButton>
        <BaseButton
          v-if="showTitleField"
          size="small"
          variant="invisible"
          @click.native="cancelEdit"
        >
          {{ $t('functional.cancel') }}
        </BaseButton>
      </div>
    </template>
  </SummaryItem>
</template>

<script setup>

import ValidationError from '@/components/library/error/ValidationError/ValidationError.vue';
import SummaryItem from '@/components/library/summary/SummaryItem/SummaryItem.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import { computed, ref } from 'vue';
import { useErrors, useUpdateWithValidation, useValidation } from '@/composables/envelopes/validation';
import { NameUpdate } from '@/store/modules/envelopes/models/Envelope';
import { useCreateUpdateEnvelope } from '@/composables/envelopes';
import i18n from '@/locales';
import { useRoute } from 'vue-router/composables';

const route = useRoute();

const props = defineProps({
  envelope: {
    required: true,
    type: Object,
  },
  envelopeSent: {
    required: true,
    type: Boolean,
  },
});

const emit = defineEmits(['editBeingMade']);

const showTitleField = ref(false);
const title = ref(props.envelope.title);

const nameUpdate = computed(() => ({ title: title.value }));
const {
  data, errors, validate,
} = useValidation(
  nameUpdate,
  NameUpdate,
);

const {
  updateEnvelope,
  isUpdatingEnvelope,
} = useCreateUpdateEnvelope(route.params.fingerprint);
const update = useUpdateWithValidation(updateEnvelope, data, validate);
const { hasErrors, getError } = useErrors(errors);

const nameButtonText = computed(() => (!showTitleField.value ? i18n.t('functional.edit') : i18n.t('functional.save')));

async function handleName() {
  if (showTitleField.value) {
    await update();
  }
  showTitleField.value = !showTitleField.value;
  emit('editBeingMade', showTitleField.value);
}

function cancelEdit() {
  showTitleField.value = false;
  // Restore the existing title
  title.value = props.envelope.title;
  validate();

  emit('editBeingMade', false);
}
</script>
